/**
 * Step validation and parsing utilities
 */
import { INTRO_STEP_ID, REVIEW_STEP_ID, REVIEW_URL_PATH } from './constants';

/**
 * Checks if a step is a special step (intro or review)
 * @param {number|string} stepId - Step ID to check
 * @returns {boolean} True if special step
 */
export const isSpecialStep = (stepId) => isIntroStep(stepId) || isReviewStep(stepId);

/**
 * Checks if a step is the intro step
 * @param {number|string} stepId - Step ID to check
 * @returns {boolean} True if intro step
 */
export const isIntroStep = (stepId) => Number(stepId) === INTRO_STEP_ID;

/**
 * Checks if a step is the review step
 * @param {number|string} stepId - Step ID to check
 * @returns {boolean} True if review step
 */
export const isReviewStep = (stepId) =>
  Number(stepId) === REVIEW_STEP_ID || stepId === REVIEW_URL_PATH;

/**
 * Returns true when a step has substeps worth rendering as a separate list.
 * Skips the auto-wrapped single-substep case (where a step without real
 * substeps is normalized to one substep that mirrors the parent), but keeps
 * synthetic groupings (e.g. patient_checkin Past Forms) where the lone
 * substep represents a different navigation target than the parent.
 *
 * @param {Object} step
 * @returns {boolean}
 */
export const hasDistinctSubsteps = (step) => {
  if (!Array.isArray(step?.substeps) || step.substeps.length === 0) return false;
  if (step.substeps.length > 1) return true;
  const only = step.substeps[0];
  return only?.substep_id !== step.step_id;
};

/**
 * Whether the sidebar should render a step's substep list/affordances.
 *
 * @param {Object} step
 * @returns {boolean}
 */
export const shouldRenderSubsteps = (step) =>
  Array.isArray(step?.substeps) &&
  (step.substeps.length > 1 || (step.substeps.length === 1 && step.showSingleSubstep === true));

/**
 * Converts a URL step_id parameter to the actual step ID
 * Handles special cases: undefined/empty → intro, 'review' → review step
 * @param {string|undefined} urlStepId - Step ID from URL params
 * @returns {number|string} Actual step ID
 */
export const parseUrlStepId = (urlStepId) => {
  if (!urlStepId) return INTRO_STEP_ID;
  if (urlStepId === REVIEW_URL_PATH) return REVIEW_STEP_ID;
  return urlStepId;
};
